import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import "./Items.css";
import { Package, Plus, Pencil, Trash2 } from "lucide-react";
import ItemModal from "./ItemsModel";
import DeleteItemModal from "./DeleteModel";
import CafeHeader from "../KOTTracker/Header/Header";

const BASE_URL = "https://fliplyn-b-c.fly.dev";

export default function KanchiItems() {
  const { categoryId } = useParams();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);

  /* ================= FETCH ITEMS ================= */

  const fetchItems = useCallback(async () => {
    if (!categoryId) return;

    try {
      setLoading(true);

      // ✅ NEW CORRECT API
      const res = await axios.get(
        `${BASE_URL}/cafe/items/category/${categoryId}/items`
      );

      setItems(res.data);
    } catch (err) {
      console.error("Fetch Items Error:", err);
    } finally {
      setLoading(false);
    }
  }, [categoryId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  /* ================= CREATE / UPDATE ================= */

  const handleSave = async (formData, itemId) => {
    try {
      setSaveLoading(true);

      if (itemId) {
        // UPDATE
        await axios.put(
          `${BASE_URL}/cafe/items/${itemId}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } else {
        // CREATE
        await axios.post(
          `${BASE_URL}/cafe/items/`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }

      await fetchItems();
      setShowModal(false);
      setEditingItem(null);

    } catch (err) {
      console.error("Save Item Error:", err.response?.data || err);
    } finally {
      setSaveLoading(false);
    }
  };

  /* ================= AVAILABILITY PATCH ================= */

  const handleAvailabilityToggle = async (item) => {
    try {
      setAvailabilityLoading(item.id);

      const formData = new URLSearchParams();
      formData.append("is_available", !item.is_available);

      const res = await axios.patch(
        `${BASE_URL}/cafe/items/${item.id}/availability`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      setItems((prev) =>
        prev.map((i) =>
          i.id === item.id
            ? { ...i, is_available: res.data.is_available }
            : i
        )
      );
    } catch (err) {
      console.error("Availability Update Error:", err);
    } finally {
      setAvailabilityLoading(null);
    }
  };

  /* ================= DELETE ================= */

  const handleDelete = async () => {
    if (!deleteItem) return;

    try {
      setDeleteLoading(true);

      await axios.delete(`${BASE_URL}/cafe/items/${deleteItem.id}`);

      await fetchItems();
      setDeleteItem(null);
    } catch (err) {
      console.error("Delete Error:", err);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <CafeHeader />

      <div className="km-items-wrapper">
        <div className="km-items-card">

          {/* HEADER */}
          <div className="km-items-header">
            <div className="km-header-left">
              <Package size={26} />
              <h1>Items</h1>
            </div>

            <div className="km-header-right">
              <div className="km-total-pill">
                Total Items: {items.length}
              </div>

              <button
                className="km-add-btn"
                onClick={() => {
                  setEditingItem(null);
                  setShowModal(true);
                }}
              >
                <Plus size={16} /> Add Item
              </button>
            </div>
          </div>

          {/* TABLE */}
          <div className="km-table-scroll">
            <div className="km-table-header">
              <div>Image</div>
              <div>Name</div>
              <div>Price</div>
              <div>GST</div>
              <div>Final</div>
              <div>Type</div>
              <div>Available</div>
              <div>Actions</div>
            </div>

            {loading ? (
              <div className="km-loading">Loading...</div>
            ) : items.length === 0 ? (
              <div className="km-empty">No items found</div>
            ) : (
              items.map((item) => (
                <div key={item.id} className="km-table-row">

                  {/* IMAGE */}
                  <div className="km-image-box">
                    {item.image_url && (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="km-item-img"
                      />
                    )}
                  </div>

                  {/* NAME */}
                  <div>{item.name}</div>

                  {/* PRICE */}
                  <div>₹{item.price}</div>

                  {/* GST */}
                  <div>{item.gst}%</div>

                  {/* FINAL */}
                  <div>₹{item.final_price}</div>

                  {/* TYPE */}
                  <div>
                    <span
                      className={
                        item.is_veg
                          ? "km-veg-badge"
                          : "km-nonveg-badge"
                      }
                    >
                      {item.is_veg ? "Veg" : "Non-Veg"}
                    </span>
                  </div>

                  {/* AVAILABILITY */}
                  <div>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={item.is_available}
                        onChange={() =>
                          handleAvailabilityToggle(item)
                        }
                        disabled={
                          availabilityLoading === item.id
                        }
                      />
                      <span className="slider"></span>
                    </label>
                  </div>

                  {/* ACTIONS */}
                  <div className="km-actions">
                    <button
                      className="km-edit-btn"
                      onClick={() => {
                        setEditingItem(item);
                        setShowModal(true);
                      }}
                    >
                      <Pencil size={14} /> Edit
                    </button>

                    <button
                      className="km-delete-btn"
                      onClick={() => setDeleteItem(item)}
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* MODALS */}
        <ItemModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingItem(null);
          }}
          onSave={handleSave}
          itemData={editingItem}
          categoryId={categoryId}
          loading={saveLoading}
        />

        <DeleteItemModal
          isOpen={!!deleteItem}
          onClose={() => setDeleteItem(null)}
          onConfirm={handleDelete}
          loading={deleteLoading}
          itemName={deleteItem?.name}
        />
      </div>
    </>
  );
}